import { useCallback, useEffect, useState } from "react";
import { motion, useReducedMotion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ChevronDown, Menu, Quote } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { api, imgUrl, imgUrlW, handleFromUrl } from "@/lib/api";
import { Reveal, EASE } from "@/components/luxe/Reveal";
import { LuxeMonogramMark } from "@/components/luxe/LuxeMonogramMark";
import { PortraitPlaceholderPlate } from "@/components/luxe/PortraitPlaceholderPlate";
import { LuxeSectionHeader } from "@/components/luxe/LuxeSectionHeader";
import { MomentsCarousel } from "@/components/luxe/MomentsCarousel";
import { CinematicIntro, INTRO_DURATION } from "@/components/luxe/CinematicIntro";
import { LuxeMarquee } from "@/components/luxe/LuxeMarquee";
import { ScrollProgress } from "@/components/luxe/ScrollProgress";
import { CountUp } from "@/components/luxe/CountUp";
import { TikTokShowcase } from "@/components/luxe/TikTokShowcase";
import { VALUE_ICONS, LINK_ICONS, normalizeUrl, TikTokIcon, InstagramIcon, ThreadsIcon } from "@/components/luxe/icons";

const FollowButtons = ({ socials, prefix, dark = false, stack = false }) => {
  const primary = dark
    ? "bg-[hsl(350_60%_97%)] text-[hsl(345_60%_16%)] hover:bg-[hsl(var(--gold-soft))]"
    : "bg-primary text-primary-foreground hover:bg-[hsl(345_55%_32%)]";
  const secondary = dark
    ? "border-[hsl(345_35%_70%/0.5)] text-[hsl(350_60%_97%)] hover:border-[hsl(var(--gold))] hover:text-[hsl(var(--gold))]"
    : "border-foreground/25 text-foreground hover:border-[hsl(var(--gold))] hover:bg-card";
  return (
    <div className={`flex flex-col gap-3 ${stack ? "sm:flex-row lg:flex-col" : "sm:flex-row"}`}>
      {socials.tiktok && (
        <a
          data-testid={`${prefix}-follow-tiktok-button`}
          href={socials.tiktok}
          target="_blank"
          rel="noopener noreferrer"
          className={`group inline-flex h-12 items-center justify-center gap-2.5 whitespace-nowrap rounded-full px-7 text-sm font-bold tracking-[-0.01em] shadow-[var(--shadow-soft)] transition-colors duration-300 active:scale-[0.98] ${primary}`}
        >
          <TikTokIcon /> Follow TikTok
          <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
        </a>
      )}
      {socials.instagram && (
        <a
          data-testid={`${prefix}-follow-instagram-button`}
          href={socials.instagram}
          target="_blank"
          rel="noopener noreferrer"
          className={`group inline-flex h-12 items-center justify-center gap-2.5 whitespace-nowrap rounded-full border px-7 text-sm font-bold tracking-[-0.01em] transition-colors duration-300 active:scale-[0.98] ${secondary}`}
        >
          <InstagramIcon /> Follow Instagram
          <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
        </a>
      )}
    </div>
  );
};

const Nav = ({ c, links }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <header
      data-testid="site-nav"
      className={`sticky top-0 z-40 border-b transition-colors duration-500 ${
        scrolled ? "border-border/80 bg-background/95 backdrop-blur-md" : "border-transparent bg-background"
      }`}
    >
      <div className="luxe-container flex h-16 items-center justify-between sm:h-20">
        <a href="#top" data-testid="nav-home-monogram" className="flex items-center gap-3">
          <LuxeMonogramMark letters={c.profile.monogram || "SS"} size="sm" />
          <span className="hidden font-display text-lg sm:inline">{c.profile.name}</span>
        </a>
        <nav className="hidden items-center gap-9 md:flex">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.id}`}
              className="group relative text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              {l.label}
              <span className="absolute -bottom-1.5 left-0 h-px w-full origin-left scale-x-0 bg-[hsl(var(--gold))] transition-transform duration-300 group-hover:scale-x-100" />
            </a>
          ))}
          <a
            href="#ikuti"
            data-testid="nav-follow-cta"
            className="inline-flex h-10 items-center rounded-full bg-primary px-5 text-sm font-bold tracking-[-0.01em] text-primary-foreground transition-colors duration-300 hover:bg-[hsl(345_55%_32%)]"
          >
            Follow
          </a>
        </nav>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" data-testid="nav-open-menu-button" className="md:hidden" aria-label="Open menu">
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-[82vw] max-w-sm bg-background">
            <SheetTitle className="sr-only">Menu</SheetTitle>
            <SheetDescription className="sr-only">Site navigation</SheetDescription>
            <div className="mt-8 flex flex-col">
              <LuxeMonogramMark letters={c.profile.monogram || "SS"} />
              <div className="mt-10 flex flex-col gap-1">
                {links.map((l, i) => (
                  <a
                    key={l.href}
                    href={l.href}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-link-${l.id}`}
                    className="flex items-baseline gap-4 border-b border-border/70 py-4 font-display text-2xl"
                  >
                    <span className="text-xs font-bold text-gold">0{i + 1}</span>
                    {l.label}
                  </a>
                ))}
              </div>
              <a
                href="#ikuti"
                onClick={() => setOpen(false)}
                data-testid="mobile-nav-follow-cta"
                className="mt-10 inline-flex h-12 items-center justify-center rounded-full bg-primary text-xs font-semibold uppercase tracking-[0.24em] text-primary-foreground"
              >
                Follow
              </a>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
};

const MaskLine = ({ children, delay, reduce, className = "" }) => (
  <span className="block overflow-hidden pb-[0.06em]">
    <motion.span
      className={`block ${className}`}
      initial={reduce ? false : { y: "105%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = ({ c, d = 0 }) => {
  const reduce = useReducedMotion();
  const { scrollY } = useScroll();
  const imgY = useTransform(scrollY, [0, 900], [0, reduce ? 0 : 90]);
  const words = (c.profile.name || "").trim().split(/\s+/);
  const first = words[0] || "";
  const rest = words.slice(1).join(" ");
  const heroLocal = /^\/api\/images\//.test(c.profile.hero_image || "");
  const heroSrc = heroLocal ? imgUrl("/api/hero-image") : imgUrl(c.profile.hero_image);

  return (
    <section id="top" data-testid="hero-section" className="relative overflow-hidden bg-background">
      <div className="hero-wash pointer-events-none absolute inset-x-0 top-0 h-[70%]" aria-hidden="true" />

      {/* When the cinematic intro is playing (d > 0) its curtain does the reveal, so the
          portrait is painted immediately underneath (faster LCP). Otherwise clip-reveal it. */}
      <motion.div
        initial={reduce || d > 0 ? false : { clipPath: "inset(0 0 100% 0)" }}
        animate={{ clipPath: "inset(0 0 0% 0)" }}
        transition={{ duration: 1.3, ease: [0.76, 0, 0.24, 1], delay: d }}
        className="relative aspect-[4/5] w-full overflow-hidden bg-secondary sm:aspect-[4/3] lg:absolute lg:inset-y-0 lg:right-0 lg:aspect-auto lg:w-[46%]"
      >
        <motion.div style={{ y: imgY }} className="absolute inset-[-6%_0_-6%_0]">
          <motion.div
            className="h-full w-full"
            initial={reduce ? false : { scale: 1.18 }}
            animate={{ scale: 1 }}
            transition={{ duration: 2.4, ease: [0.22, 1, 0.36, 1], delay: d }}
          >
            {c.profile.hero_image ? (
              <img
                data-testid="hero-portrait-image"
                src={heroSrc}
                srcSet={heroLocal ? `${imgUrlW("/api/hero-image", 640)} 640w, ${imgUrlW("/api/hero-image", 828)} 828w, ${imgUrl("/api/hero-image")} 1080w` : undefined}
                sizes={heroLocal ? "(min-width: 1024px) 46vw, 100vw" : undefined}
                alt={`${c.profile.name} — ${c.profile.title || "portrait"}`}
                width="1080"
                height="1250"
                fetchPriority="high"
                decoding="async"
                className="h-full w-full object-cover object-[50%_18%]"
              />
            ) : (
              <PortraitPlaceholderPlate letters={c.profile.monogram || "SS"} testId="hero-portrait-upload-placeholder" />
            )}
          </motion.div>
        </motion.div>
        <span className="pointer-events-none absolute inset-x-0 bottom-0 h-28 bg-[linear-gradient(to_top,hsl(var(--background)),transparent)] lg:hidden" aria-hidden="true" />
        {c.profile.location && (
          <motion.div
            initial={reduce ? false : { opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: d + 1.1 }}
            className="absolute bottom-6 right-5 rounded-2xl border border-border bg-card px-5 py-3.5 shadow-[var(--shadow-soft)] lg:bottom-10 lg:right-10"
          >
            <p className="eyebrow text-[9px] text-muted-foreground">Based in</p>
            <p data-testid="hero-location" className="mt-1 font-display text-lg">{c.profile.location}</p>
          </motion.div>
        )}
      </motion.div>

      <div className="luxe-container relative lg:flex lg:min-h-[min(calc(100vh-80px),880px)] lg:items-center">
        <div className="pb-4 pt-4 sm:pt-10 lg:w-[54%] lg:py-24 lg:pr-10">
          <motion.div initial={reduce ? false : { opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.9, ease: EASE, delay: d + 0.35 }} className="flex items-center gap-4">
            <motion.span className="gold-rule w-12 origin-left" initial={reduce ? false : { scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: 0.9, ease: EASE, delay: d + 0.35 }} />
            <span data-testid="hero-eyebrow" className="eyebrow text-muted-foreground">{c.profile.eyebrow}</span>
          </motion.div>
          <h1
            data-testid="hero-name"
            className="mt-7 font-display text-[3.2rem] font-black leading-[0.95] !tracking-[-0.05em] sm:text-7xl sm:leading-[0.95] lg:text-[5.6rem] lg:leading-[0.93] xl:text-[6.2rem]"
          >
            <MaskLine reduce={reduce} delay={d + 0.45}>{first}</MaskLine>
            {rest && (
              <MaskLine reduce={reduce} delay={d + 0.6}>
                <span className="text-gold">{rest}</span>
                <span className="text-foreground">.</span>
              </MaskLine>
            )}
          </h1>
          <motion.p
            data-testid="hero-title"
            initial={reduce ? false : { opacity: 0, letterSpacing: "0.5em" }}
            animate={{ opacity: 1, letterSpacing: "0.2em" }}
            transition={{ duration: 1.4, ease: EASE, delay: d + 0.9 }}
            className="mt-8 text-xs font-bold uppercase tracking-[0.2em] text-gold sm:text-sm"
          >
            {c.profile.title}
          </motion.p>
          <motion.p
            data-testid="hero-tagline"
            initial={reduce ? false : { opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: EASE, delay: d + 1.05 }}
            className="mt-5 max-w-[34rem] text-base leading-[1.8] text-muted-foreground sm:text-lg"
          >
            {c.profile.tagline}
          </motion.p>
        </div>
      </div>
    </section>
  );
};

const validChildren = (l) => (l.children || []).filter((ch) => ch.label && ch.url);

const linkClasses = (hi) =>
  `group flex min-h-[72px] w-full items-center gap-4 rounded-full border p-2.5 pr-3 text-left shadow-[var(--shadow-hairline)] transition-[transform,box-shadow,border-color,background-color] duration-300 hover:-translate-y-0.5 hover:shadow-[var(--shadow-soft)] active:scale-[0.99] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
    hi ? "border-primary bg-primary text-primary-foreground hover:bg-[hsl(345_55%_30%)]" : "border-border bg-card text-foreground hover:border-[hsl(var(--gold)/0.6)]"
  }`;

const LinkInner = ({ l, hi, trailing }) => {
  const Icon = LINK_ICONS[l.icon] || LINK_ICONS.link;
  return (
    <>
      <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full ${hi ? "bg-[hsl(345_50%_36%)] text-primary-foreground" : "bg-secondary text-primary"}`}>
        <Icon className="h-5 w-5" />
      </span>
      <span className="min-w-0 flex-1 text-left">
        <span className="block break-words text-base font-bold leading-tight tracking-[-0.01em]">{l.label}</span>
        {l.subtitle && <span className={`mt-0.5 block truncate text-xs ${hi ? "text-[hsl(345_40%_86%)]" : "text-muted-foreground"}`}>{l.subtitle}</span>}
      </span>
      {trailing && (
        <span className={`flex h-10 w-8 shrink-0 items-center justify-center rounded-full transition-transform sm:w-10 duration-300 ${hi ? "text-primary-foreground" : "text-muted-foreground group-hover:text-primary"}`}>
          {trailing}
        </span>
      )}
    </>
  );
};

const LinkAnchor = ({ l, testId, hi }) => {
  const href = normalizeUrl(l.url);
  const external = /^https?:/i.test(href);
  return (
    <a data-testid={testId} href={href} target={external ? "_blank" : undefined} rel={external ? "noopener noreferrer" : undefined} className={linkClasses(hi)}>
      <LinkInner l={l} hi={hi} trailing={<ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />} />
    </a>
  );
};

const LinkGroup = ({ l, index }) => {
  const [open, setOpen] = useState(false);
  const reduce = useReducedMotion();
  const hi = !!l.highlight;
  const kids = validChildren(l);
  return (
    <div>
      <button type="button" data-testid={`quick-link-${index}`} aria-expanded={open} onClick={() => setOpen((o) => !o)} className={linkClasses(hi)}>
        <LinkInner l={l} hi={hi} trailing={<ChevronDown className={`h-5 w-5 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="kids"
            data-testid={`quick-link-${index}-group`}
            initial={reduce ? false : { height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={reduce ? undefined : { height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="overflow-hidden"
          >
            <div className="ml-3 space-y-3 border-l-2 border-[hsl(var(--gold)/0.35)] pl-3 pt-3 sm:ml-7 sm:pl-5">
              {kids.map((ch, k) => (
                <LinkAnchor key={ch.id || k} l={ch} hi={!!ch.highlight} testId={`quick-link-${index}-child-${k}`} />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const ComingSoonLink = ({ l, testId }) => {
  const hi = !!l.highlight;
  return (
    <div
      data-testid={testId}
      aria-disabled="true"
      className={`flex min-h-[72px] w-full items-center gap-4 rounded-full border border-dashed p-2.5 pr-4 ${
        hi ? "border-[hsl(345_45%_55%/0.6)] bg-primary/90 text-primary-foreground" : "border-[hsl(var(--gold)/0.45)] bg-card/60 text-foreground"
      }`}
    >
      <LinkInner l={l} hi={hi} trailing={null} />
      <span
        className={`shrink-0 rounded-full px-2.5 py-1.5 text-[9px] font-bold uppercase tracking-[0.12em] sm:px-3 sm:text-[10px] sm:tracking-[0.16em] ${
          hi ? "bg-[hsl(350_60%_97%)] text-primary" : "bg-secondary text-primary"
        }`}
      >
        Coming soon
      </span>
    </div>
  );
};

const QuickLinks = ({ c }) => {
  // Build segments: a "heading" item starts a new segment.
  const segments = [];
  let current = { heading: null, items: [] };
  (c.links || []).forEach((l) => {
    if (l.type === "heading") {
      if (current.heading || current.items.length) segments.push(current);
      current = { heading: l, items: [] };
    } else if (l.label) {
      current.items.push(l);
    }
  });
  if (current.heading || current.items.length) segments.push(current);
  const visible = segments.filter((sg) => sg.items.length > 0);
  if (!visible.length) return null;

  let linkIdx = -1;
  let segNo = 0;
  return (
    <section id="links" data-testid="quick-links-section" className="pb-4 pt-14 sm:pb-6 sm:pt-20">
      <div className="mx-auto w-full max-w-[640px] px-5 sm:px-8">
        <LuxeSectionHeader eyebrow={c.headings?.links_eyebrow || "Quick Links"} title={c.headings?.links_title} align="center" className="!mb-10" />
        <div className="space-y-7 sm:space-y-8">
          {visible.map((sg, si) => {
            if (sg.heading?.label) segNo += 1;
            return (
              <div key={sg.heading?.id || `seg-${si}`} data-testid={`quick-links-segment-${si}`}>
                {sg.heading?.label && (
                  <Reveal className="mb-3 text-center">
                    <span className="text-xs font-bold text-gold">{String(segNo).padStart(2, "0")}</span>
                    <h3 data-testid={`quick-links-segment-title-${si}`} className="mt-0.5 font-display text-xl sm:text-2xl">{sg.heading.label}</h3>
                    {sg.heading.subtitle && <p className="mt-1.5 text-sm text-muted-foreground">{sg.heading.subtitle}</p>}
                  </Reveal>
                )}
                <ul className="space-y-3 sm:space-y-4">
                  {sg.items.map((l) => {
                    linkIdx += 1;
                    const i = linkIdx;
                    const tid = `quick-link-${i}`;
                    let node;
                    if (validChildren(l).length > 0) node = <LinkGroup l={l} index={i} />;
                    else if (l.url) node = <LinkAnchor l={l} hi={!!l.highlight} testId={tid} />;
                    else node = <ComingSoonLink l={l} testId={tid} />;
                    return (
                      <Reveal as="li" key={l.id || i} delay={0.04}>
                        {node}
                      </Reveal>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const Stats = ({ items }) => (
  <section data-testid="stats-strip" className="border-y border-border/80 bg-card">
    <div className="luxe-container grid grid-cols-2 sm:grid-cols-[repeat(auto-fit,minmax(0,1fr))] sm:grid-flow-col">
      {items.map((s, i) => (
        <Reveal key={i} delay={i * 0.06} className={`px-2 py-8 text-center sm:py-10 ${i > 0 ? "sm:border-l sm:border-border/80" : ""}`}>
          <p data-testid="stats-item-value" className="font-display text-3xl sm:text-4xl"><CountUp value={s.value} /></p>
          <p className="eyebrow mt-3 text-[10px] text-muted-foreground">{s.label}</p>
        </Reveal>
      ))}
    </div>
  </section>
);

const About = ({ c, index }) => (
  <section id="kisah" data-testid="about-section" className="py-20 sm:py-28">
    <div className="luxe-container">
      <LuxeSectionHeader index={index} eyebrow={c.headings?.about_eyebrow || "The Story"} title={c.about.title} />
      <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
        <Reveal className="lg:col-span-7">
          {(c.about.story || "").split(/\n\s*\n/).filter(Boolean).map((p, i) => (
            <p
              key={i}
              data-testid="about-story-paragraph"
              className={`max-w-[62ch] leading-[1.9] text-muted-foreground ${i === 0 ? "font-display text-2xl font-semibold leading-[1.45] !tracking-[-0.02em] text-foreground sm:text-[1.7rem]" : "mt-6 text-base sm:text-[1.05rem]"}`}
            >
              {p}
            </p>
          ))}
        </Reveal>
        {c.about.quote && (
          <Reveal delay={0.15} className="lg:col-span-5">
            <figure data-testid="about-quote" data-luxe-noise="true" className="rounded-3xl border border-border bg-card p-8 shadow-[var(--shadow-hairline)] sm:p-10">
              <div className="relative z-[1]">
                <Quote className="h-7 w-7 text-gold" strokeWidth={1.2} />
                <blockquote className="mt-6 font-display text-2xl font-semibold leading-[1.4] !tracking-[-0.02em] sm:text-[1.6rem]">“{c.about.quote}”</blockquote>
                <figcaption className="mt-8 flex items-center gap-4">
                  <span className="gold-rule w-10" />
                  <span className="font-display text-2xl text-gold">{c.about.signature}</span>
                </figcaption>
              </div>
            </figure>
          </Reveal>
        )}
      </div>
    </div>
  </section>
);

const Journey = ({ c, index }) => {
  const j = c.journey || {};
  const items = (j.items || []).filter((it) => it.year || it.title || it.text);
  if (!items.length) return null;
  const linkHref = j.link_url ? normalizeUrl(j.link_url) : "";
  const external = /^https?:/i.test(linkHref);
  return (
    <section id="journey" data-testid="journey-section" className="border-t border-border/80 py-20 sm:py-28">
      <div className="luxe-container grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-28">
            <LuxeSectionHeader index={index} eyebrow={j.eyebrow || "The Journey"} title={j.title} className="!mb-6" />
            {j.intro && (
              <Reveal>
                <p data-testid="journey-intro" className="max-w-[46ch] text-base leading-[1.85] text-muted-foreground sm:text-[1.05rem]">{j.intro}</p>
              </Reveal>
            )}
            {j.highlight_value && (
              <Reveal delay={0.1}>
                <div data-testid="journey-highlight" className="mt-10 flex items-center gap-6 rounded-3xl border border-border bg-card p-6 shadow-[var(--shadow-soft)] sm:p-7">
                  <CountUp value={j.highlight_value} testId="journey-highlight-count" className="min-w-[1ch] font-display text-[5.5rem] font-black leading-[0.8] !tracking-[-0.06em] text-primary sm:text-[6.5rem]" />
                  <span className="min-w-0">
                    {j.highlight_label && <span className="eyebrow block text-gold">{j.highlight_label}</span>}
                    {j.highlight_text && <span className="mt-2 block text-lg font-bold leading-snug tracking-[-0.01em] sm:text-xl">{j.highlight_text}</span>}
                  </span>
                </div>
              </Reveal>
            )}
          </div>
        </div>
        <div className="lg:col-span-7">
          <ol className="relative">
            <span className="absolute bottom-3 left-[7px] top-3 w-px bg-[hsl(var(--gold)/0.35)]" aria-hidden="true" />
            {items.map((it, i) => {
              const last = i === items.length - 1;
              return (
                <Reveal as="li" key={it.id || i} delay={0.05} className={`relative pl-10 sm:pl-12 ${last ? "" : "pb-10 sm:pb-12"}`}>
                  <span
                    className={`absolute left-0 top-[0.55rem] h-[15px] w-[15px] rounded-full border-2 border-primary ${last ? "bg-primary" : "bg-background"}`}
                    aria-hidden="true"
                  />
                  <div data-testid={`journey-item-${i}`}>
                    <p className="font-display text-4xl leading-none text-gold sm:text-5xl">{it.year}</p>
                    {it.title && <h3 className="mt-3 text-lg font-bold tracking-[-0.01em] sm:text-xl">{it.title}</h3>}
                    {it.text && <p className="mt-2 max-w-[56ch] leading-[1.8] text-muted-foreground">{it.text}</p>}
                    {it.link_url && (
                      <a
                        data-testid={`journey-item-link-${i}`}
                        href={normalizeUrl(it.link_url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group mt-4 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-card px-4 py-2 text-sm font-bold text-primary transition-colors duration-300 hover:border-primary hover:bg-primary hover:text-primary-foreground"
                      >
                        {it.link_label || it.link_url}
                        <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                      </a>
                    )}
                  </div>
                </Reveal>
              );
            })}
          </ol>
          {(j.closing || (j.link_label && linkHref)) && (
            <Reveal className="mt-12 rounded-3xl border border-border bg-card p-7 shadow-[var(--shadow-hairline)] sm:p-9">
              {j.closing && <p data-testid="journey-closing" className="font-display text-2xl leading-[1.25] sm:text-[1.75rem]">{j.closing}</p>}
              {j.link_label && linkHref && (
                <a
                  data-testid="journey-follow-link"
                  href={linkHref}
                  target={external ? "_blank" : undefined}
                  rel={external ? "noopener noreferrer" : undefined}
                  className="group mt-5 inline-flex items-center gap-2 text-sm font-bold text-primary"
                >
                  {j.link_label}
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </a>
              )}
            </Reveal>
          )}
        </div>
      </div>
    </section>
  );
};

const Values = ({ c, index }) => (
  <section id="nilai" data-testid="values-section" className="border-t border-border/80 bg-[hsl(348_60%_95%)] py-20 sm:py-28">
    <div className="luxe-container">
      <LuxeSectionHeader index={index} eyebrow={c.headings?.values_eyebrow || "Philosophy"} title={c.values.title} />
      <div className="grid grid-cols-1 gap-5 md:grid-cols-3 md:gap-6">
        {(c.values.items || []).map((v, i) => {
          const Icon = VALUE_ICONS[v.icon] || VALUE_ICONS.gem;
          return (
            <Reveal key={i} delay={i * 0.08}>
              <div data-testid="values-pillar-card" className="group h-full rounded-3xl border border-border bg-card p-8 shadow-[var(--shadow-hairline)] transition-[box-shadow,transform] duration-500 hover:-translate-y-1 hover:shadow-[var(--shadow-lift)] sm:p-10">
                <div className="flex items-center justify-between">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full border border-[hsl(var(--gold)/0.6)] text-gold transition-colors duration-500 group-hover:bg-[hsl(var(--gold))] group-hover:text-[hsl(350_70%_98%)]">
                    <Icon className="h-5 w-5" strokeWidth={1.4} />
                  </span>
                  <span className="text-sm font-bold text-muted-foreground">0{i + 1}</span>
                </div>
                <h3 className="mt-10 font-display text-3xl">{v.title}</h3>
                <p className="mt-4 leading-[1.8] text-muted-foreground">{v.text}</p>
              </div>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

const Gallery = ({ c, index }) => (
  <section id="galeri" data-testid="gallery-section" className="overflow-hidden py-20 sm:py-28">
    <div className="luxe-container">
      <LuxeSectionHeader index={index} eyebrow={c.headings?.gallery_eyebrow || "Lifestyle"} title={c.headings?.gallery_title} className={c.headings?.gallery_subtitle ? "!mb-5" : ""} />
      {c.headings?.gallery_subtitle && (
        <Reveal>
          <p data-testid="gallery-subtitle" className="mb-10 max-w-[52ch] text-base leading-[1.85] text-muted-foreground sm:mb-12 sm:text-[1.05rem]">{c.headings.gallery_subtitle}</p>
        </Reveal>
      )}
      <MomentsCarousel items={c.gallery || []} monogram={c.profile.monogram || "SS"} />
    </div>
  </section>
);

const Featured = ({ c, index }) => (
  <section id="tiktok" data-testid="tiktok-section" className="on-dark bg-espresso py-20 text-[hsl(350_60%_97%)] sm:py-28" data-luxe-noise="true">
    <div className="luxe-container relative z-[1]">
      <div className="flex flex-col justify-between gap-6 sm:flex-row sm:items-end">
        <LuxeSectionHeader index={index} eyebrow={c.headings?.featured_eyebrow || "On TikTok"} title={c.headings?.featured_title} light className="!mb-0" />
        {c.socials.tiktok && (
          <a
            data-testid="tiktok-section-profile-link"
            href={c.socials.tiktok}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.24em] text-[hsl(var(--gold))]"
          >
            {handleFromUrl(c.socials.tiktok, "TikTok")}
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </a>
        )}
      </div>
      <div className="mt-12">
        <TikTokShowcase items={c.featured} />
      </div>
    </div>
  </section>
);

const Statement = ({ c }) => (
  <section data-testid="statement-quote" className="bg-[linear-gradient(135deg,hsl(350_60%_97%),hsl(345_70%_92%))] py-20 sm:py-28">
    <Reveal className="luxe-container max-w-[960px] text-center">
      <span className="mx-auto block h-12 w-px bg-[hsl(var(--gold))]" aria-hidden="true" />
      <blockquote className="mt-10 font-display text-3xl leading-[1.15] sm:text-4xl sm:leading-[1.15] lg:text-[3.4rem] lg:leading-[1.1]">“{c.statement.quote}”</blockquote>
      {c.statement.author && <p className="eyebrow mt-10 text-muted-foreground">{c.statement.author}</p>}
    </Reveal>
  </section>
);

const FinalCta = ({ c }) => (
  <section id="ikuti" data-testid="final-cta-section" className="py-20 sm:py-28">
    <div className="luxe-container">
      <Reveal>
        <div data-luxe-noise="true" className="overflow-hidden rounded-[2rem] border border-border bg-card px-6 py-14 shadow-[var(--shadow-soft)] sm:px-14 sm:py-20">
          <div className="relative z-[1] grid grid-cols-1 items-end gap-10 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-4">
                <span className="gold-rule w-10" />
                <span className="eyebrow text-muted-foreground">{c.headings?.final_eyebrow || "Stay connected"}</span>
              </div>
              <h2 className="mt-6 font-display text-4xl leading-[1.05] sm:text-5xl lg:text-6xl">{c.final_cta.title}</h2>
              <p className="mt-6 max-w-[40ch] leading-[1.8] text-muted-foreground">{c.final_cta.subtitle}</p>
            </div>
            <div className="lg:col-span-5 lg:flex lg:justify-end">
              <div className="flex flex-col gap-3 lg:min-w-[280px]">
                <FollowButtons socials={c.socials} prefix="final" stack />
                <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground">
                  {c.socials.tiktok && <span>TikTok {handleFromUrl(c.socials.tiktok, "")}</span>}
                  {c.socials.instagram && <span>Instagram {handleFromUrl(c.socials.instagram, "")}</span>}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

const Footer = ({ c }) => (
  <footer data-testid="site-footer" className="on-dark bg-espresso text-[hsl(345_40%_86%)]">
    <div className="luxe-container flex flex-col gap-8 py-12 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-4">
        <LuxeMonogramMark letters={c.profile.monogram || "SS"} size="sm" className="!text-[hsl(350_60%_97%)]" />
        <div>
          <p className="font-display text-lg text-[hsl(350_60%_97%)]">{c.profile.name}</p>
          <p className="text-xs tracking-[0.2em] uppercase text-[hsl(345_25%_72%)]">{c.profile.title}</p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        {c.socials.tiktok && (
          <a data-testid="footer-tiktok-link" href={c.socials.tiktok} target="_blank" rel="noopener noreferrer" aria-label="TikTok" className="flex h-11 w-11 items-center justify-center rounded-full border border-[hsl(345_35%_70%/0.35)] transition-colors hover:border-[hsl(var(--gold))] hover:text-[hsl(var(--gold))]">
            <TikTokIcon />
          </a>
        )}
        {c.socials.instagram && (
          <a data-testid="footer-instagram-link" href={c.socials.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="flex h-11 w-11 items-center justify-center rounded-full border border-[hsl(345_35%_70%/0.35)] transition-colors hover:border-[hsl(var(--gold))] hover:text-[hsl(var(--gold))]">
            <InstagramIcon />
          </a>
        )}
        {c.socials.threads && (
          <a data-testid="footer-threads-link" href={c.socials.threads} target="_blank" rel="noopener noreferrer" aria-label="Threads" className="flex h-11 w-11 items-center justify-center rounded-full border border-[hsl(345_35%_70%/0.35)] transition-colors hover:border-[hsl(var(--gold))] hover:text-[hsl(var(--gold))]">
            <ThreadsIcon />
          </a>
        )}
      </div>
    </div>
    <div className="border-t border-[hsl(345_35%_70%/0.18)]">
      <div className="luxe-container flex flex-col gap-3 py-6 text-[11px] leading-relaxed text-[hsl(345_25%_72%)] sm:flex-row sm:justify-between">
        <p data-testid="footer-risk-disclaimer" className="max-w-[70ch]">{c.footer.disclaimer}</p>
        <p>© {new Date().getFullYear()} {c.profile.name}</p>
      </div>
    </div>
  </footer>
);

const LoadingScreen = () => (
  <div data-testid="landing-loading" className="flex min-h-screen flex-col items-center justify-center gap-5 bg-background">
    <motion.div animate={{ opacity: [0.35, 1, 0.35] }} transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}>
      <LuxeMonogramMark letters="SS" size="lg" />
    </motion.div>
  </div>
);

const INTRO_KEY = "ss_intro_seen";
const SITE_URL = (process.env.REACT_APP_SITE_URL || window.location.origin).replace(/\/$/, "");

export default function Landing() {
  const [c, setC] = useState(null);
  const [error, setError] = useState(false);
  const [startedAt] = useState(() => performance.now());
  const [showIntro, setShowIntro] = useState(() => {
    try {
      if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return false;
      return !sessionStorage.getItem(INTRO_KEY);
    } catch (e) {
      return true;
    }
  });
  const introWanted = showIntro;
  const finishIntro = useCallback(() => {
    try {
      sessionStorage.setItem(INTRO_KEY, "1");
    } catch (e) {
      /* ignore */
    }
    setShowIntro(false);
  }, []);
  const [heroDelay, setHeroDelay] = useState(0);

  const load = () => {
    setError(false);
    api
      .get("/content")
      .then((r) => {
        if (introWanted) {
          const remaining = (startedAt + (INTRO_DURATION + 0.25) * 1000 - performance.now()) / 1000;
          setHeroDelay(Math.max(0, remaining));
        }
        setC(r.data);
      })
      .catch(() => setError(true));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, []);

  useEffect(() => {
    if (!c) return;
    const title = c.seo?.title || c.profile.name;
    const desc = c.seo?.description || "";
    document.title = title;
    const setMeta = (attr, key, value) => {
      if (!value) return;
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", value);
    };
    setMeta("name", "description", desc);
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", desc);
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", desc);
    setMeta("name", "robots", "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1");
    let canon = document.head.querySelector('link[rel="canonical"]');
    if (!canon) {
      canon = document.createElement("link");
      canon.setAttribute("rel", "canonical");
      document.head.appendChild(canon);
    }
    canon.setAttribute("href", `${SITE_URL}/`);
  }, [c]);

  const intro = (
    <AnimatePresence>
      {introWanted && (
        <CinematicIntro
          key="intro"
          letters={c?.profile?.monogram || "SS"}
          name={c?.profile?.name || "Safira Sallehudin"}
          title={c?.profile?.title || "Markets \u2022 Mindset \u2022 Legacy"}
          onDone={finishIntro}
        />
      )}
    </AnimatePresence>
  );

  let body;
  if (error) {
    body = (
      <div data-testid="landing-error" className="flex min-h-screen flex-col items-center justify-center gap-6 px-6 text-center">
        <LuxeMonogramMark letters="SS" size="lg" />
        <p className="text-muted-foreground">Sorry, this page could not be loaded.</p>
        <Button data-testid="landing-retry-button" onClick={load} className="rounded-full px-8">Try again</Button>
      </div>
    );
  } else if (!c) {
    body = <LoadingScreen />;
  } else {
    const s = c.sections || {};
    const showStats = s.stats && (c.stats || []).filter((x) => x.value || x.label).length > 0;
    const showFeatured = s.featured && (c.featured || []).length > 0;

    const order = [];
    if (s.about) order.push("about");
    const showJourney = s.journey && (c.journey?.items || []).length > 0;
    if (showJourney) order.push("journey");
    if (s.values) order.push("values");
    if (s.gallery) order.push("gallery");
    if (showFeatured) order.push("featured");
    const idx = (k) => `0${order.indexOf(k) + 1}`;

    const links = [
      s.about && { id: "kisah", href: "#kisah", label: "Story" },
      showJourney && { id: "journey", href: "#journey", label: "Journey" },
      s.values && { id: "nilai", href: "#nilai", label: "Values" },
      s.gallery && { id: "galeri", href: "#galeri", label: "Gallery" },
      showFeatured && { id: "tiktok", href: "#tiktok", label: "TikTok" },
    ].filter(Boolean);

    body = (
      <div data-testid="landing-page" className="min-h-screen bg-background">
        <ScrollProgress />
        <Nav c={c} links={links} />
        <main>
          <Hero c={c} d={heroDelay} />
          {s.marquee !== false && <LuxeMarquee words={(c.marquee || []).map((w) => (w || "").trim())} />}
          {s.links && <QuickLinks c={c} />}
          {showStats && <Stats items={c.stats.filter((x) => x.value || x.label)} />}
          {s.about && <About c={c} index={idx("about")} />}
          {showJourney && <Journey c={c} index={idx("journey")} />}
          {s.values && <Values c={c} index={idx("values")} />}
          {s.gallery && <Gallery c={c} index={idx("gallery")} />}
          {showFeatured && <Featured c={c} index={idx("featured")} />}
          {s.statement && c.statement?.quote && <Statement c={c} />}
          {s.final_cta && <FinalCta c={c} />}
        </main>
        <Footer c={c} />
      </div>
    );
  }

  return (
    <>
      {intro}
      {body}
    </>
  );
}
